import { useMemo, useState } from "react";
import usePartners from "../../../hooks/usePartners";
import { z } from "zod";
import { FieldApi, FieldValidators, ReactFormExtendedApi, useForm } from "@tanstack/react-form";
import useTrustedForm from "@/hooks/useTrustedForm";
import useToastStore from "@/stores/toasts";
import usePostLeadMutation from "../../../hooks/usePostLeadMutation";
import { useMixpanel } from "@/third-party/mixpanel/useMixpanel";
import { errorValidtorMap } from "./state.helpers";
import { getWindowData, HOME_BIZ_QUESITON_ID } from "./getWindowData";
import { questionNodeMetaSchema, useSQFGraph, type StaticGraphNodeId, type StaicNodeMeta, QuestionNodeMeta } from "./useSQFGraph";
import { flatRecord } from "@/utils/types";
import { map } from "@/utils/object/map";
import { replacePlaceholders } from "@/utils/string/replace-placeholders";
import { stripHtml } from "@/utils/html";
import debounce from "lodash.debounce";

export default function useServiceQuoteFlow({ onNext, onPrev }: Params) {
  // --- PREPARE DATA ---
  const { homeowner_info, service_info, sourceId, defaultLocation, defaultZipSource, tcpaText: windowTcpaText, preferenceTo } = useMemo(() => getWindowData(), []);

  // NOTE: this is display in the contarctors check box label AND send to the backend as part of the tcpa_compliance_text.
  const CONTRACTORS_SELECTION_LABEL = "Select the below pros to receive a custom estimate.";

  // --- HOOKS ---
  const mixpanel = useMixpanel();
  const toasts = useToastStore();

  const {
    formRef: trustedFormRef,
    getTrustedFormValues,
  } = useTrustedForm();
  const postLeadMutation = usePostLeadMutation();

  // ---------------------------
  // -------- GRAPH ------------
  // ---------------------------
  const hideZipStep = defaultZipSource === "search";
  const graph = useSQFGraph({
    initialValues: {
      root: hideZipStep ? 1 : 0,
    }
  });

  // ---------------------------
  // -------- STATE ------------
  // ---------------------------
  // this is needed to simulate some validation behavior for some of the fields, see NX-3875
  const [fieldSubmittionCount, setFieldSubmittionCount] = useState<Record<string, number>>({});
  const updateFieldSubmitCount = (fieldName: keyof typeof fieldSubmittionCount) => {
    setFieldSubmittionCount(prev => {
      const currentCount = (prev[fieldName] ?? 0) + 1;
      return {
        ...prev,
        [fieldName]: currentCount,
      };
    });
  }
  // here you should add all the parameters that are linked to a field
  // think about if you move this field from step N to step M, will you change or move this parameter
  // if yes it is probably a parameter you want to add in the graph
  type Questions = Array<{ questionId: string, answerId: string | null }>;
  type SelectedContractors = { id: string; name: string; selected: boolean; }[]
  const formConfig = {
    zip: {
      defaultValue: defaultLocation.zip ?? "",
      guard: () => false,
      transformer: (v: string) => v.replaceAll(/[^0-9]/g, "").slice(0, 5),
      validator: errorValidtorMap<string>({
        "Please enter a valid zip code": (v) => v.length != 5,
      }),
      props: (field: FieldApiAny<string>) => {
        const meta = field.state.meta;
        const isFresh = (fieldSubmittionCount[field.name] ?? 0) === 0;
        return {
          name: field.name,
          value: field.state.value,
          onValueChange: guardTransformExec(field.handleChange, formConfig.zip.guard, formConfig.zip.transformer),
          onBlur: () => {
            field.handleBlur();
            if (field.state.value.length > 0 && defaultValues.zip !== field.state.value) {
              updateFieldSubmitCount(field.name);
            }
          },
          errorMessage: meta.errors[0] || "",
          // INFO: this will be invalid if we try to "sumbit" (next) this, or if we blurred from the field
          isInvalid: Boolean(!isFresh && meta.isBlurred && meta.errors.length),
        } as const;
      },
    },
    address: {
      defaultValue: homeowner_info?.address ?? "",
      guard: () => false,
      transformer: (v: string) => v,
      validator: errorValidtorMap<string>({}),
      props: (field: FieldApiAny<string>) => {
        const meta = field.state.meta;
        return {
          name: field.name,
          value: field.state.value,
          onValueChange: guardTransformExec(field.handleChange, formConfig.address.guard, formConfig.address.transformer),
          onBlur: field.handleBlur,
          errorMessage: meta.errors[0] || "",
          isInvalid: Boolean((meta.isBlurred && meta.errors.length) || (meta.isPristine && meta.errors.length)),
          showIcon: (Boolean(field.state.value) && !meta.errors.length),
        } as const;
      },
    },
    questions: {
      defaultValue: [
        (hideZipStep
          ? { questionId: (graph.steps.at(0)?.activeChild?.meta as QuestionNodeMeta).questionId, answerId: null }
          : undefined)
      ].filter(Boolean) as Questions,
      props: (form: ReactFormExtendedApi<{ questions: Questions }>, index: number) => {
        const field = form.state.values.questions[index];
        if (!field) {
          throw new Error(`index '${index}' must be in bounds of questions array.`);
        }
        const { questionId, answerId } = field;
        return {
          radioGroup: {
            name: `questions[${questionId}]`,
            value: answerId,
            onValueChange: (newAnswerId: Questions[number]['answerId']) => {
              form.replaceFieldValue("questions", index, { questionId, answerId: newAnswerId })
            },
          },
        };
      },
    },
    name: {
      defaultValue: homeowner_info?.f_name ?? "",
      guard: () => false,
      transformer: (v: string) => v,
      validator: errorValidtorMap({
        "This field is required.": (v: string) => v.length === 0,
      }),
      props: (field: FieldApiAny<string>) => {
        const meta = field.state.meta;
        return {
          name: field.name,
          value: field.state.value,
          onValueChange: guardTransformExec(field.handleChange, formConfig.name.guard, formConfig.name.transformer),
          onBlur: field.handleBlur,
          errorMessage: meta.errors[0] || "",
          isInvalid: Boolean((meta.isBlurred && meta.errors.length) || (meta.isPristine && meta.errors.length)),
          showIcon: ((meta.isTouched && meta.isPristine)) || (Boolean(field.state.value) && !meta.errors.length),
        } as const;
      },
    },
    phone: {
      defaultValue: homeowner_info?.n_phone ?? "",
      guard: () => false,
      transformer: (v: string) => v.replaceAll(/[^0-9]/g, "").slice(0, 10),
      validator: errorValidtorMap<string>({
        "This field is required.": (v) => v.length === 0,
        "Please enter a valid number": (v) => v.length < 10,
      }),
      props: (field: FieldApiAny<string>) => {
        const meta = field.state.meta;
        return {
          name: field.name,
          value: field.state.value,
          onValueChange: guardTransformExec(field.handleChange, formConfig.phone.guard, formConfig.phone.transformer),
          onBlur: field.handleBlur,
          errorMessage: meta.errors[0] || "",
          isInvalid: Boolean((meta.isBlurred && meta.errors.length) || (meta.isPristine && meta.errors.length)),
          showIcon: ((meta.isTouched && meta.isPristine)) || (Boolean(field.state.value) && !meta.errors.length),
        } as const;
      },
    },
    email: {
      defaultValue: homeowner_info?.email ?? "",
      guard: () => false,
      transformer: (v: string) => v,
      validator: errorValidtorMap<string>({
        "This field is required.": (v) => v.length === 0,
        "Please enter a valid email address.": (v) =>
          !z.string().email().safeParse(v).success,
      }),
      props: (field: FieldApiAny<string>) => {
        const meta = field.state.meta;
        return {
          name: field.name,
          value: field.state.value,
          onValueChange: guardTransformExec(field.handleChange, formConfig.email.guard, formConfig.email.transformer),
          onBlur: field.handleBlur,
          errorMessage: meta.errors[0] || "",
          isInvalid: Boolean((meta.isBlurred && meta.errors.length) || (meta.isPristine && meta.errors.length)),
          showIcon: (meta.isTouched && meta.isPristine) || (Boolean(field.state.value) && !meta.errors.length),
        } as const;
      },
    },
    additionalInfo: {
      defaultValue: "",
      guard: () => false,
      transformer: (v: string) => v,
      validator: errorValidtorMap<string>({}),
      props: (field: FieldApiAny<string>) => {
        const meta = field.state.meta;
        return {
          name: field.name,
          value: field.state.value,
          onValueChange: guardTransformExec(field.handleChange, formConfig.additionalInfo.guard, formConfig.additionalInfo.transformer),
          onBlur: field.handleBlur,
          errorMessage: meta.errors[0] || "",
          isInvalid: Boolean(meta.isBlurred && meta.errors.length),
        } as const;
      },
    },
    selectedContractors: {
      defaultValue: [] as SelectedContractors,
      guard: () => false,
      transformer: (v: SelectedContractors) => v,
      validator: errorValidtorMap<SelectedContractors>({}),
      props: (field: FieldApiAny<SelectedContractors>) => {
        const meta = field.state.meta;
        const onValueChange = (newSelected: SelectedContractors[number]['id'][]) => {
          const contractors = field.state.value.map(contractor => ({ ...contractor, selected: newSelected.includes(contractor.id) }));
          return guardTransformExec(field.handleChange, formConfig.selectedContractors.guard, formConfig.selectedContractors.transformer)(contractors);
        };
        return {
          name: field.name,
          value: field.state.value.filter(c => c.selected).map(c => c.id), // we return just the selected ids
          onValueChange,
          onBlur: field.handleBlur,
          errorMessage: meta.errors[0] || "",
          isInvalid: meta.errors.length > 0,
          label: CONTRACTORS_SELECTION_LABEL,
        } as const;
      },
    },
    // INTERNAL STATE
    is_partner: {
      defaultValue: false,
    },
  };

  const defaultValues = map(formConfig, (v) => v.defaultValue) as flatRecord<
    "defaultValue",
    typeof formConfig
  >;


  const questionsStateToRecord = (questions: Questions): Record<string, string> => {
    return Object.fromEntries(questions.map(q => ([q.questionId, q.answerId as string] as const)));
  }

  const partnersMutation = usePartners({
    onSuccess: (data) => {
      if (!data) {
        // this mean we are redirecting!
        return;
      }
      form.setFieldValue("selectedContractors", () => data.pros.map(pro => ({ id: pro.id, name: pro.name, selected: true })))
      form.setFieldValue("is_partner", () => data.is_partner ?? false);
    }
  });

  const tcpaText = (partnersMutation.isSuccess && partnersMutation.data.type === "stay")
    ? partnersMutation.data.data.tcpa_text ?? windowTcpaText
    : windowTcpaText;

  /**
   * call this when you want to submit everything to the backend
   */
  const submit = async (value: typeof defaultValues) => {
    const tf = getTrustedFormValues();
    const allContractors = value.selectedContractors;
    const allContractorsForPayload = allContractors.map((c, idx) => ({
      id: Number(c.id),
      accepted: c.selected,
      position: idx + 1, // This is By Spec: should send 1-based position
    }));
    const tcpaContractorsText = CONTRACTORS_SELECTION_LABEL
      + "\n"
      + allContractors.map(c => c.name).join(", ")
      + "\n";
    const hasContractors = allContractors.length > 0;
    const tcpa_compliance_text = (hasContractors ? tcpaContractorsText : "") + stripHtml(tcpaText);
    const payload = {
      f_name: value.name,
      n_phone: value.phone,
      zip: value.zip,
      email: value.email,
      address: value.address,
      question: questionsStateToRecord(value.questions),
      service: service_info.id,
      preference_to: preferenceTo,
      source_id: sourceId,
      is_partner: value.is_partner,
      tcpa_compliance_text,
      add_info: value.additionalInfo,
      viewed_contractors: allContractorsForPayload,
      xxTrustedFormCertUrl: tf.xxTrustedFormCertUrl!, // FIXME: what should happen if xxTrustedFormCertUrl is undefined
      xxTrustedFormToken: tf.xxTrustedFormToken,
      xxTrustedFormPingUrl: tf.xxTrustedFormPingUrl,
    };
    const res = await postLeadMutation.mutateAsync(payload);

    if (res.type === "redirect_url") {
      window.location.href = res.redirect_url;
      await new Promise(() => { }); // let the loading state displayed while redirecting
    } else if (res.type === "validation_errors") {
      // Show error if we have one
      if (res.response?.error_messages && res.response.error_messages.length > 0) {
        toasts.addMulti(res.response.error_messages.map(e => ({ variant: "error", title: e })));
      }
      // Inejct error in each field
      if (res.errors.zip_code) {
        form.setFieldMeta('zip', (meta) => ({
          ...meta,
          errorMap: { onChange: res.errors.zip_code }
        }));
      }
      if (res.errors.phone) {
        form.setFieldMeta("phone", (meta) => ({
          ...meta,
          errorMap: { onChange: res.errors.phone }
        }));
      }
      if (res.errors.name) {
        form.setFieldMeta("name", (meta) => ({
          ...meta,
          errorMap: { onChange: res.errors.name }
        }));
      }
      if (res.errors.submitted_email) {
        form.setFieldMeta("email", (meta) => ({
          ...meta,
          errorMap: { onChange: res.errors.submitted_email }
        }));
      }
      return false;
    }
    return true;
  }

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      return await submit(value);
    }
  });



  const trackNext = (nodeId: string) => {
    const node = graph.dag.find(n => n.id === nodeId);
    if (!node) {
      throw new Error(`nodeId ${nodeId} must exist in graph`);
    }
    mixpanel.track(
      "Click - OB HO - Next button click",
      {
        // FIXME: this will fail quietly if developer change the meta in the graph
        page: node.meta?.pageName,
        from_step: node.meta?.title,
        to_step: (node.children.length === 1 ? node.children[0] : node.activeChild)?.meta?.title,
        zip: form.state.values.zip,
        category: service_info.name,
      }
    );
  }

  const trackPrev = (nodeId: string) => {
    const node = graph.dag.find(n => n.id === nodeId);
    if (!node) {
      throw new Error(`nodeId ${nodeId} must exist in graph`);
    }
    onPrev?.(nodeId);
    mixpanel.track(
      "Click - OB HO - Previous button click",
      {
        // FIXME: this will fail quietly if developer change the meta in the graph
        page: node.meta?.pageName,
        from_step: node.meta?.title,
        to_step: node.parent?.meta?.title,
        category: service_info.name,
        element: 'Previous button',
        component: 'Wizard'
      }
    );
  }

  const trackError = (nodeId: string, fieldName: keyof typeof defaultValues, error_text: string) => {
    const node = graph.dag.find(n => n.id === nodeId);
    if (!node) {
      throw new Error(`nodeId ${nodeId} must exist in graph`);
    }

    // Because _reasons_, we track this `error_type` ONLY for those fields
    // yay specs 🙄
    const mapFieldToType = {
      phone: 'Phone Number',
      email: 'Email Address',
      name: 'Full Name',
    } satisfies Partial<Record<typeof fieldName, string>>;

    mixpanel.track('Error Annotated', {
      // FIXME: this will fail quietly if developer change the meta in the graph
      page: node.meta?.pageName,
      // here again, if we don't have it in the mapFieldToType we don't send it... pfff...
      error_type: mapFieldToType[fieldName],
      error_text,
    });
  }


  /**
   * this function will:
   * - resolve the graph to the next step, so it will be visible
   *   - the graph itself will know to reset the nodes (visibile steps)
   * - create the new question sub state if needed
   * - remove the questions that are not need now if answer was changed
   *
   *   for static node, just pass the current NodeId
   *   for question node also pass the chosen answerId
   */
  const advanceGraphAndCreateQuestion = (nodeId: string, answerId?: string) => {
    const node = graph.dag.find(n => n.id === nodeId);
    if (!node) {
      throw new Error(`nodeId ${nodeId} must exist in graph`);

    }

    // if we are at the end of the graph, we call submit
    if (node.isLeaf()) {
      return form.handleSubmit();
    }

    if (answerId) {
      // if we got an answerId, that's should mean the current node is a question
      const questionMeta = questionNodeMetaSchema.safeParse(node.meta);
      if (!questionMeta.success) {
        throw new Error(`Expected Node '${node.id}' to be of type question`);
      }
      const meta = questionMeta.data;
      const answer = meta.answers.find(answer => answer.id === answerId);
      if (!answer) {
        throw new Error(`answerId '${answerId}' must be defined as a answers (in meta and graph) of node '${node.id}'!`);
      }
      const nextNodeId = answer.nextQuestionId[0];
      if (!nextNodeId) {
        // this mean we are at the end of the QNA, and we should just resolve to the first child.
        graph.resolveByIndex(node.id, 0);
        return;
      }
      const nextNode = node.children.find(n => n.id === nextNodeId)!;

      if (!nextNode.meta?.questionId) {
        throw new Error(`expected 'question' children ${node.id} to have a question children with id ${nextNode.id}`);
      }
      const nextQuestionId = nextNode.meta.questionId;
      const currentFormQuestionIndex = form.state.values.questions.findIndex(q => q.answerId === answerId);
      if (currentFormQuestionIndex === -1) {
        throw new Error(`answerId '${answerId}' must be found in the form.questions at this point`);
      }
      if (nextNodeId === node.activeChild?.id) {
        // Do nothing if you select/click-next with same answer
        return;
      }

      // In this case we need to reset the future answered question, in case we re-answered and changed path
      resetQuestionFromIndex(currentFormQuestionIndex);
      // First we add the next question to the form
      form.pushFieldValue("questions", { questionId: nextQuestionId, answerId: null })
      // Go to next Child
      graph.resolveById(node.id, nextNodeId);
    } else {
      // FIXME: do we need this if now?
      if (nodeId === "is_interested" && hideZipStep) {
        // in the case that we need to skip the zip step, we resolve to the first question
        const questionIndex = node.children.findIndex(n => n.id !== "zip");
        if (questionIndex === -1) {
          throw new Error("graph broken, we need to have a link between is_interested and zip");
        }
        const questionId = node.children[questionIndex]!.meta?.questionId;
        if (!questionId) {
          throw new Error("is_interested node should have one children which is a question!");
        }
        if (!form.state.values.questions.find(q => q.questionId === questionId)) {
          form.pushFieldValue("questions", { questionId, answerId: null })
        }
        graph.resolveByIndex(node.id, questionIndex);
        return;
      }
      if (node.children[0]?.meta?.questionId) { // isNextNodeAQuestion
        const questionId = node.children[0].meta.questionId;
        if (!form.state.values.questions.find(q => q.questionId === questionId)) {
          form.pushFieldValue("questions", { questionId, answerId: null })
        }
        // then we resolve the graph - which will make the render display the question
      }
      // In both case of next child is static or dynamic (question) we resolve to it.
      graph.resolveByIndex(node.id, 0);
    }
  }

  /** this is to affect the STATE not the graph */
  const resetQuestionFromIndex = (index: number) => {
    const questionsLength = form.state.values.questions.length;
    if (index > questionsLength) {
      throw new Error("Can't pass an index for a question state that is bigger that the form.questions.length");
    }
    const questionId = form.state.values.questions[index]!.questionId;
    const countToRemove = questionsLength - 1 - index;
    // NOTE: we need to remove from the end, because removing from the start shift the array, and then it does not work
    for (let i = questionsLength - 1; i > questionsLength - 1 - countToRemove; i--) {
      form.removeFieldValue("questions", i)
    }
    graph.reset(`q-${questionId}`);
  }


  // here you should add all the parameters that are linked to a step
  // it should be related to the shape of the graph/flow
  // for example if when selecting an answer we should autoForward or not to the next step
  const steps = graph.steps.map((node, index) => {
    const id = node.id as StaticGraphNodeId;
    switch (id) {
      case "root": {
        return null;
      }
      case "zip": {
        const nodeMeta = node.meta as StaicNodeMeta;
        // we need to call this when we resolve (next, onEnter) and also when we blur
        const reset = debounce(() => {
          if (partnersMutation.variables?.zip && partnersMutation.variables.zip !== form.state.values.zip) {
            partnersMutation.reset();
            const homeBizQuesionIndex = form.state.values.questions.findIndex(q => q.questionId === String(HOME_BIZ_QUESITON_ID));
            const questionId = form.state.values.questions[homeBizQuesionIndex]!.questionId;
            resetQuestionFromIndex(homeBizQuesionIndex);
            form.replaceFieldValue("questions", homeBizQuesionIndex, { questionId, answerId: null });
          }
        }, 300);
        const resolve = async () => {
          updateFieldSubmitCount("zip");
          reset.flush();
          const errors = await form.validateField('zip', "change");
          if (errors.length) {
            trackError(id, "zip", errors.join("\n"));
            return;
          }
          advanceGraphAndCreateQuestion(node.id);
          onNext?.(node.id);
          return true;
        };
        return {
          id,
          nodeId: id,
          type: id,
          title: () => replacePlaceholders(nodeMeta.title, { service_name: service_info.contractors }).split("\n"),
          pageName: nodeMeta.pageName,
          hidden: hideZipStep,
          fields: {
            zip: {
              validators: {
                onChange: formConfig.zip.validator,
              } satisfies FieldValidators<typeof defaultValues, "zip", undefined, undefined, typeof defaultValues.zip>,
              props: formConfig.zip.props,
            }
          },
          canContinue: (_: string) => {
            return true;
          },
          resolve,
          reset,
          next: async () => {
            trackNext(node.id);
            return await resolve();
          },
        } as const;
      }
      case "additional-info": {
        const nodeMeta = node.meta as StaicNodeMeta;
        const resolve = async () => {
          const errors = await form.validateField('additionalInfo', "change");
          if (errors.length) {
            trackError(id, "additionalInfo", errors.join("\n"));
            return false;
          }
          advanceGraphAndCreateQuestion(node.id);
          onNext?.(node.id);
          return true;
        };
        return {
          id,
          nodeId: id,
          type: id,
          title: nodeMeta.title.split("\n"),
          pageName: nodeMeta.pageName,
          fields: {
            additionalInfo: {
              validators: {
                onChange: formConfig.additionalInfo.validator,
              } satisfies FieldValidators<typeof defaultValues, "additionalInfo", undefined, undefined, typeof defaultValues.additionalInfo>,
              props: formConfig.additionalInfo.props,
            }
          },
          resolve,
          canContinue: (_: string) => {
            return true;
          },
          next: async () => {
            trackNext(node.id);
            return await resolve();
          }
        } as const;
      }
      case "homeowner-full-name": {
        const nodeMeta = node.meta as StaicNodeMeta;
        const resolve = async () => {
          const errors = await form.validateField('name', "change");
          if (errors.length) {
            trackError(id, "name", errors.join("\n"));
            return false;
          }
          advanceGraphAndCreateQuestion(node.id);
          onNext?.(node.id);
          return true;
        };
        return {
          id,
          nodeId: id,
          type: id,
          title: nodeMeta.title.split("\n"),
          pageName: nodeMeta.pageName,
          fields: {
            name: {
              validators: {
                onChange: formConfig.name.validator,
              } satisfies FieldValidators<typeof defaultValues, "name", undefined, undefined, typeof defaultValues.name>,
              props: formConfig.name.props,
            }
          },
          resolve,
          canContinue: (_: string) => {
            return true;
          },
          next: async () => {
            trackNext(node.id);
            return await resolve();
          }
        } as const;
      }
      case "homeowner-address": {
        const isHomeownerEmailPreFilled = Boolean(homeowner_info?.email);
        const nodeMeta = node.meta as StaicNodeMeta;
        const resolve = async () => {
          let errorsCount = 0;
          for (const fieldName of ["address", "email"] as const) {
            const errors = await form.validateField(fieldName, "change");
            if (errors.length) {
              trackError(id, fieldName, errors.join("\n"));
              errorsCount += errors.length;
            }
          }
          if (errorsCount > 0) return false;
          advanceGraphAndCreateQuestion(node.id);
          onNext?.(node.id);
          return true;
        };
        return {
          id,
          nodeId: id,
          type: id,
          title: (homeowner_name: string) => replacePlaceholders(nodeMeta.title, { homeowner_name }).split("\n"),
          pageName: nodeMeta.pageName,
          fields: {
            address: {
              validators: {
                onChange: formConfig.address.validator,
              } satisfies FieldValidators<typeof defaultValues, "address", undefined, undefined, typeof defaultValues.address>,
              props: formConfig.address.props,
            },
            email: {
              hidden: isHomeownerEmailPreFilled,
              validators: {
                onChange: formConfig.email.validator,
              } satisfies FieldValidators<typeof defaultValues, "email", undefined, undefined, typeof defaultValues.email>,
              props: formConfig.email.props,
            },
          },
          resolve,
          canContinue: (_: {}) => {
            // we let the user click the button, even in bad state,
            // he will see errors
            return true;
          },
          next: async () => {
            trackNext(node.id);
            return await resolve();
          }
        } as const;
      }
      case "homeowner-phone": {
        const nodeMeta = node.meta as StaicNodeMeta;
        const resolve = async () => {
          let errorsCount = 0;
          for (const fieldName of ["phone", "selectedContractors"] as const) {
            const errors = await form.validateField(fieldName, "change");
            if (errors.length) {
              trackError(id, fieldName, errors.join("\n"));
              errorsCount += errors.length;
            }
          }
          if (errorsCount > 0) return false;
          // In this current implementaion "homeowner-phone" is the last step,
          // but the advacneGraphAndCreateQuestion function will call submit for us
          advanceGraphAndCreateQuestion(node.id);
          onNext?.(node.id);
          return true;
        };
        return {
          id,
          nodeId: id,
          type: id,
          fields: {
            phone: {
              validators: {
                onChange: formConfig.phone.validator,
              } satisfies FieldValidators<typeof defaultValues, "phone", undefined, undefined, typeof defaultValues.phone>,
              props: formConfig.phone.props,
            },
            selectedContractors: {
              validators: {
                onChange: formConfig.selectedContractors.validator,
              } satisfies FieldValidators<typeof defaultValues, "selectedContractors", undefined, undefined, typeof defaultValues.selectedContractors>,
              props: formConfig.selectedContractors.props,
            },
          },
          title: nodeMeta.title.split("\n"),
          pageName: nodeMeta.pageName,
          resolve,
          canContinue: (_: any) => {
            // we let the user click the button, even in bad state,
            // he will see errors
            return true;
          },
          next: async () => {
            // THIS IS THE LAST STEP... so we don't track next, but submit
            mixpanel.track(
              "Click - OB HO - Submit",
              {
                page: nodeMeta.pageName,
                address: form.state.values.address,
                full_name: form.state.values.name,
                phone: form.state.values.phone,
              }
            );
            return await resolve();
          }
        } as const;
      }
      default: {
        // The questions node don't have a static id, so he we parse if we got a question node,
        // and work with it
        const questionMeta = questionNodeMetaSchema.safeParse(node.meta);
        if (!questionMeta.success) {
          throw new Error(`Node with id '${id}' is not expected as a non-static node, please implement in useServiceQuoteFlow`);
        }
        const nodeMeta = questionMeta.data;
        const questionId = nodeMeta.questionId;
        const resolve = async () => {
          const questionValue = form.state.values.questions.find(q => q.questionId === questionId);
          if (!questionValue) {
            throw new Error(`Expected at this point to have this question ${questionId} defined as a form state.`);
          }
          if (!questionValue.answerId) {
            console.warn("clicked on next without selecting an answer. this is probably wrong");
            return;
          }
          const answerIndex = nodeMeta.answers.findIndex(ans => ans.id === questionValue.answerId);
          const answerText = nodeMeta.answers[answerIndex]!.text;
          mixpanel.track(
            "OB HO - Wizard Answer selected",
            {
              page: nodeMeta.pageName,
              category: service_info.name,
              answer_selected_text: answerText,
              question_text: nodeMeta.title,
              questions_tree_parent_id: questionValue.questionId,
              item_list_location: answerIndex + 1,
            }
          );

          {// reset all next qna questions
            graph.steps.slice(index).forEach(step => step.reset());
          }

          if (questionId === String(HOME_BIZ_QUESITON_ID)) {
            // NOTE: the type of answerId is that it could be undefined, but we are after the check so everything is defined
            const questions = questionsStateToRecord(form.state.values.questions);
            const res = await partnersMutation.mutateAsync({
              pageName: nodeMeta.pageName,
              zip: form.state.values.zip,
              questions,
              service: service_info.id,
            });
            if (res.type === "redirect") {
              // in case we redirect we don't want to advance the graph
              return;
            }
          }
          advanceGraphAndCreateQuestion(node.id, questionValue.answerId);
          onNext?.(node.id);
          return true;
        }


        return {
          id: nodeMeta.questionId,
          nodeId: id,
          // NOTE: we use the type as "QNA" so we can know the shape of this object
          // but the "id" is still unique
          type: "QNA" as const,
          options: nodeMeta.answers,
          props: formConfig.questions.props,
          autoForward: true,
          title: nodeMeta.title.split("\n"),
          pageName: nodeMeta.pageName,
          isLoading: partnersMutation.isPending,
          canContinue: (value: Questions, index: number) => {
            const current = value[index];
            if (current?.questionId === String(HOME_BIZ_QUESITON_ID)) {
              return Boolean(current.answerId);
            }
            return Boolean(value[index]?.answerId);
          },
          resolve,
          next: async () => {
            trackNext(node.id);
            return await resolve();
          },
        }
      }
    }
  }).filter(Boolean) satisfies Step[];

  const getPageNameByNodeId = (nodeId: string) => {
    const step = steps.find(s => s.nodeId === nodeId);
    const pageName = step?.pageName;
    if (!pageName) {
      if (step) {
        throw new Error(`nodeId ${nodeId} should have a pageName`);
      } else {
        throw new Error(`nodeId ${nodeId} should be available in steps`);
      }
    }
    return pageName;
  }

  const isFirstStep = (nodeId: string) => {
    const node = graph.dag.find(n => n.id === nodeId);
    if (!node) {
      throw new Error(`isFirstStep - nodeId ${nodeId} must exist in graph`);
    }
    // HACK: because we use a null root node in the dag, we need to take care of that
    // it's probably better to add multihead capabilities to the dag
    return node.isHead() || node?.parent?.isHead();
  }

  const isLastStep = (nodeId: string) => {
    const node = graph.dag.find(n => n.id === nodeId);
    if (!node) {
      throw new Error(`isFirstStep - nodeId ${nodeId} must exist in graph`);
    }
    return node.isLeaf();
  }

  return {
    progress: graph.progress,
    steps,
    form,
    tcpaText,
    trustedFormRef,
    getPageNameByNodeId,
    isFirstStep,
    isLastStep,
    trackPrev,
  };
}

export type SQFForm = ReturnType<typeof useServiceQuoteFlow>['form'];

type ReturnStepType = ReturnType<typeof useServiceQuoteFlow>['steps'][number];
/** give you the sub-type with discrimination of the returned steps type */
export type SQFStepOf<TType extends ReturnStepType['type']> = ReturnStepType & { type: TType };

/** so it will be more consistent and easy to create UI we should have a common step interface*/
type Step = {
  /** step id, not to be confused with node id of the graph*/
  id: string;
  /** node id to work with graph ids, only questions are different for now*/
  nodeId: string;
  /** can be used for discrimination */
  type: StaticGraphNodeId | "QNA";
  /** step should not be visible to user if hidden is true*/
  hidden?: boolean;
  /** if false we should not let the user continue to the next step, i.e. disable next button */
  canContinue?: (...args: any) => boolean;
  // FIXME: we need to find a more generic way for this
  // fields?: Record<string, {
  //   validators: FieldValidators<any, any, undefined, undefined, any>;
  //   props: (...args: any) => object;
  // }>;

  /** multi part (string[]) title should be render each part on another line
   * use MultiLineTitle component
   */
  title: ((...args: any) => string | string[]) | string | string[];
  pageName: string;
  /**
   * does nothing until used.
   * should declare if this step autoForawrd to the next or not
   * needs to be integrated by the UI developer
   * calling `resolve` function is probably the right choice
   */
  autoForward?: boolean;
  /** to be called when user press next button, it will track event and call resolve
   */
  next: () => Promise<boolean | void | undefined>;
  /** to be called for 'resolving' the step, this is handling pure state and graph
   * call next if you want to track next button event
   */
  resolve: () => Promise<boolean | void | undefined>;
}

type FieldApiAny<T> = FieldApi<any, any, undefined, undefined, T>;

type Params = {
  onNext?: (nodeId: string) => void;
  onPrev?: (nodeId: string) => void;
}

function guardTransformExec<TIn, TOut>(
  exec: (value: TIn) => TOut,
  guard?: (value: TIn) => boolean,
  transform?: (value: TIn) => TIn,
) {
  return function (value: TIn): TOut | undefined {
    if (guard?.(value) ?? false) {
      return;
    }
    const transformed = transform?.(value) ?? value;
    return exec(transformed);
  }
}

